import { useState, JSX, useEffect } from 'react';
import useFetch from '../../../hooks/fetch.hook';
import { StopAreaApiModel } from '../models/stop-area.api-model';
import { CommuterParkingLotApiModel } from '../models/commuter-parking-lot.api-model';
import { TicketOfficeApiModel } from '../models/ticket-office.api.model';
import { ITicketOffice } from '../../../models/ticket-office.model';
import { ICommuterParkingLot } from '../../../models/commuter-parking-lot.model';
import { IStopArea } from '../../../models/stop-area.model';
import TabListNavItem from '../../Shared/TabList/components/TabListNavItem';
import TabListTabContent from '../../Shared/TabList/components/TabListTabContent';
import TabList from '../../Shared/TabList';
import { ConfigurationService } from '../../../services/configuration.service';
import { TranslationService } from '../../../services/translation.service';
import GroupedListWithNavigation from './components/GroupedListWithNavigation';

function groupByFirstCharacterTyped<T extends { name: string }>(items: T[]): Record<string, T[]> {
  if (!items || items.length === 0) return {};
  return items.reduce((accumulator, item) => {
    const firstChar = (item.name?.[0] || '').toUpperCase();
    if (!firstChar) return accumulator;
    if (!accumulator[firstChar]) accumulator[firstChar] = [];
    accumulator[firstChar].push(item);
    return accumulator;
  }, {} as Record<string, T[]>);
}

function processReceivedStopAreaData(data: StopAreaApiModel[] | undefined): IStopArea[] {
  if (!data) return [];
  return data?.map(StopAreaApiModel.mapToBusinessModel) ?? [];
}

function processReceivedCommuterParkingLotsData(data: CommuterParkingLotApiModel[] | undefined): ICommuterParkingLot[] {
  if (!data) return [];
  return data?.map(CommuterParkingLotApiModel.mapToBusinessModel) ?? [];
}

function processReceivedTicketOfficesData(data: TicketOfficeApiModel[] | undefined): ITicketOffice[] {
  if (!data) return [];
  return data?.map(TicketOfficeApiModel.mapToBusinessModel) ?? [];
}

function StopAreaList() {
  const [{ data: getStopAreasData }] = useFetch<StopAreaApiModel[]>('/api/stop-area-search/stopareas');
  const [{ data: getCommuterParkingLotsData }] = useFetch<CommuterParkingLotApiModel[]>('/api/stop-area-search/commuterparkinglots');
  const [{ data: getTicketOfficesData }] = useFetch<TicketOfficeApiModel[]>('/api/stop-area-search/ticketoffices');
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // === VARIABLES ===
  const stopAreas = processReceivedStopAreaData(getStopAreasData);
  const commuterParkingLots = processReceivedCommuterParkingLotsData(getCommuterParkingLotsData);
  const ticketOffices = processReceivedTicketOfficesData(getTicketOfficesData);

  const groupedStopAreas = groupByFirstCharacterTyped<IStopArea>(stopAreas);
  const groupedCommuterParkingLots = groupByFirstCharacterTyped<ICommuterParkingLot>(commuterParkingLots);
  const groupedTicketOffices = groupByFirstCharacterTyped<ITicketOffice>(ticketOffices);

  // Main zones are 3000 (A), 3001 (B), and 3002 (C)
  const MAIN_ZONE_NUMBERS = [3000, 3001, 3002];

  useEffect(() => {
    setActiveTabIndex(getTabFromQuery());
  }, []);

  function getTabFromQuery(): number {
    const params = new URLSearchParams(window.location.search);
    const tab = parseInt(params.get('tab') || '0', 10);
    return isNaN(tab) ? 0 : tab;
  }

  function setTabInQuery(tabIndex: number) {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', tabIndex.toString());
    // Always preserve the hash for all tabs, so deep-linking works everywhere
    window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
  }

  //NV-7390 Only show tariff zones that are not mini zones
  function isMainZone(number: number): boolean {
    return MAIN_ZONE_NUMBERS.includes(number);
  }

  function renderStopAreaList(item: IStopArea): JSX.Element {
    let text = item.name;
    if (item.municipalityName) {
      text += ', ' + item.municipalityName;
    }
    if (item.tariffZone1 && item.tariffZone1.name) {
      text += `, ${TranslationService.get('common.tariffzone')} ${item.tariffZone1.shortName}`;
    }
    if (item.tariffZone2 && item.tariffZone2.name && isMainZone(item.tariffZone2.number)) {
      text += `, ${TranslationService.get('common.tariffzone')} ${item.tariffZone2.shortName}`;
    }
    return (
      <div>
        <a href={`${ConfigurationService.get('stoparealistpage.stopareamappage')}${item.gid}`}>{text}</a>
      </div>
    );
  }

  function renderCommuterParkingLotList(item: ICommuterParkingLot): JSX.Element {
    return (
      <div>
        {item.name}, {item.freeSpaces ? `${item.freeSpaces} lediga platser` : `${item.totalCapacity} platser`}
      </div>
    );
  }

  function renderTicketOfficeList(item: ITicketOffice): JSX.Element {
    return (
      <div>
        {item.name}, {item.location}
      </div>
    );
  }

  return (
    <div>
      <TabList
        id="stop-area-list__tab-list"
        activeIndex={activeTabIndex}
        renderTabsAsDropdownOnMobile={true}
        dropdownLabel={TranslationService.get('common.selectlisttype')}
        onSelectedChange={(tabId) => {
          setActiveTabIndex(tabId);
          setTabInQuery(tabId);
        }}
        navItems={[
          <TabListNavItem key={'nav-item-0'}>{TranslationService.get('common.stops')}</TabListNavItem>,
          <TabListNavItem key={'nav-item-1'}>{TranslationService.get('common.parkingspaces')}</TabListNavItem>,
          <TabListNavItem key={'nav-item-2'}>{TranslationService.get('common.ticketoffices')}</TabListNavItem>,
        ]}
        tabContents={[
          <TabListTabContent key={'tab-item-0'}>
            <GroupedListWithNavigation groupedItems={groupedStopAreas} renderItem={renderStopAreaList} />
          </TabListTabContent>,
          <TabListTabContent key={'tab-item-1'}>
            <GroupedListWithNavigation groupedItems={groupedCommuterParkingLots} renderItem={renderCommuterParkingLotList} focusableSections={true} />
          </TabListTabContent>,
          <TabListTabContent key={'tab-item-2'}>
            <GroupedListWithNavigation groupedItems={groupedTicketOffices} renderItem={renderTicketOfficeList} focusableSections={true} />
          </TabListTabContent>,
        ]}
      ></TabList>
    </div>
  );
}

export default StopAreaList;
